import {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames/bind';

import BetaTag from '../../../Shared/BetaTag';
import NewTag from '../../../Shared/NewTag';
import ItemsScrollBar from '../../../Shared/ItemsScrollBar';

import useSizes from '../../../../hooks/sizes';

import { toggleStoryFilters } from '../../../../actions/preferencesActions';

import StorageSvc from '../../../../services/StorageSvc';

import dcscLogo from '../../../../assets/dcsc-logo.svg';

import Styles from './styles.module.scss';

const cx = classNames.bind(Styles);

const TopicTab = ({
  tabs,
  changeTab,
  activeTab = null,
  className = '',
}, ref) => {
  const { width } = useSizes();
  const { height: promobarHeight } = useSelector((state) => state.promobar);
  const dispatch = useDispatch();
  const scrollPointRef = useRef();

  const isChatbotOpened = useSelector((state) => state.chatbot.isChatbotOpened);

  const chatbotViewWasChanged = isChatbotOpened && (width > 1024 && width < 1500);

  const tabsData = useMemo(() => (
    tabs.map((tab) => ({
      id: tab.id,
      content: (
        <>
          {tab.isDcsc && (
            <img className={Styles['dcsc-logo']} src={dcscLogo} alt="dcsc logo" />
          )}
          <span className={cx('title', { dcsc: tab.isDcsc })}>
            {tab.title}
          </span>
          {(tab.isBeta || tab.isNew) && (
            <div className={Styles.tags}>
              {tab.isBeta && <BetaTag className={Styles.tag} />}
              {tab.isNew && (
                <NewTag className={Styles.tag} />
              )}
            </div>
          )}
        </>
      ),
      active: tab.active ?? activeTab === tab.id,
      style: {
        marginRight: `${((tab.isBeta || 0) + (tab.isNew || 0)) * (width >= 2800 ? 72 : 44)}px`,
        ...(tab.isDcsc && tab.active && {
          borderBottomColor: '#926DDF',
        }),
      },
    }))
  ), [tabs, width >= 2800, activeTab, chatbotViewWasChanged]);

  const onTabChange = useCallback((id) => {
    const tabTop = scrollPointRef.current.getBoundingClientRect().top;
    if (tabTop <= 0) {
      changeTab(
        id,
        document.documentElement.scrollTop + tabTop,
      );
    } else {
      changeTab(id);
    }

    dispatch(toggleStoryFilters(false));
  }, [changeTab, dispatch]);

  useEffect(() => {
    if (typeof ref === 'function') {
      ref(scrollPointRef.current);
    } else if (ref instanceof Object && 'current' in ref) {
      ref.current = scrollPointRef.current;
    }
  }, [ref]);

  useEffect(() => {
    if (!(width > 767 || StorageSvc.getItem('mobilePopupSeen'))) {
      return;
    }

    document.querySelector(':root').style.setProperty(
      '--promobar-height',
      `${promobarHeight}px`,
    );
  }, [width, promobarHeight]);

  return (
    <>
      <div ref={scrollPointRef} style={{ backgroundClip: 'text' }} />
      <div
        className={cx('topic-tab', className)}
      >
        <ItemsScrollBar
          data={tabsData}
          onClick={onTabChange}
          itemClassName={cx('topic-tab-item')}
          scrollToSelected
          chatbotViewWasChanged={chatbotViewWasChanged}
        />
      </div>
    </>
  );
};

export default memo(forwardRef(TopicTab));
